/**
 * RTK Query mutation.unwrap() rejects with FetchBaseQueryError / Serialized shapes,
 * not usually an Error instance — (e as Error).message is often undefined.
 * API payloads may use message, detail, or error keys.
 */
export function formatRtkMutationError(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error && error.message) return error.message;
  if (!error || typeof error !== 'object') return String(error ?? 'Unknown error');

  const e = error as {
    status?: number | string;
    data?: unknown;
    error?: string;
    message?: string;
    originalStatus?: string | number;
  };

  const fromDataPayload = (data: unknown): string | undefined => {
    if (!data || typeof data !== 'object') return undefined;
    const o = data as Record<string, unknown>;
    if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
    if (typeof o.detail === 'string' && o.detail.trim()) return o.detail.trim();
    if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
    return undefined;
  };

  const dataMsg =
    typeof e.data === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(e.data) as Record<string, unknown>;
            const m = fromDataPayload(parsed);
            if (m) return m;
          } catch {
            /* body is plain text/HTML */
          }
          const stripped = e.data.replace(/<[^>]+>/g, '').trim().slice(0, 280);
          return stripped || undefined;
        })()
      : fromDataPayload(e.data);
  if (dataMsg) return dataMsg;

  if (typeof e.error === 'string' && e.error) return e.error;
  if (typeof e.message === 'string' && e.message) return e.message;

  if (e.status === 'FETCH_ERROR' || e.status === 'PARSING_ERROR') {
    return typeof e.error === 'string' && e.error ? e.error : 'Network error — please try again.';
  }
  if (e.status !== undefined && e.status !== '') {
    return typeof e.status === 'number'
      ? `Request failed (${e.status})`
      : `Request failed (${String(e.status)})`;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}
